"use client";

import { c as _c } from "react/compiler-runtime";
import { RefreshCw, Shield } from "lucide-react";
import { Field, FieldLayout } from "@/src/components/cards/common";
import { CopyableContent } from "@/src/components/copyable-content";
import { Button } from "@/src/components/shadcn/ui/button";
import { ValerieCard, type ValerieCardHeaderVariant } from "@/src/components/valerie-card";
import { getEligibilityPresentation } from "@/src/lib/clearinghouse/stedi/eligibility-presentation";
import { resolveStediEligibilityDisplay } from "@/src/lib/clearinghouse/stedi/extract-eligibility-summary";
import type { StediEligibilityCheck } from "@/src/models/stedi-eligibility-check";
import type { ReactNode } from "react";
export function RecheckButton(t0) {
  const $ = _c(7);
  const {
    onRecheck,
    isRechecking,
    label,
    pendingLabel: t1
  } = t0;
  const pendingLabel = t1 === undefined ? "Checking..." : t1;
  const t2 = `h-3 w-3 ${isRechecking ? "animate-spin" : ""}`;
  let t3;
  if ($[0] !== t2) {
    t3 = <RefreshCw className={t2} />;
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const t4 = isRechecking ? pendingLabel : label;
  let t5;
  if ($[2] !== isRechecking || $[3] !== onRecheck || $[4] !== t3 || $[5] !== t4) {
    t5 = <Button type="button" variant="outline" size="sm" className="flex h-7 items-center gap-1.5 px-2 text-xs" disabled={isRechecking} onClick={onRecheck}>{t3}{t4}</Button>;
    $[2] = isRechecking;
    $[3] = onRecheck;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  return t5;
}
function formatCheckedAt(createdAt: string) {
  return new Date(createdAt).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC"
  });
}
export function StediEligibilityActions(t0) {
  const $ = _c(13);
  const {
    check,
    onRecheck,
    isRechecking: t1
  } = t0;
  const isRechecking = t1 === undefined ? false : t1;
  if (!check) {
    let t2;
    if ($[0] !== isRechecking || $[1] !== onRecheck) {
      t2 = onRecheck ? <RecheckButton onRecheck={onRecheck} isRechecking={isRechecking} label="Check eligibility" /> : null;
      $[0] = isRechecking;
      $[1] = onRecheck;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return t2;
  }
  let t2;
  if ($[3] !== check.createdAt) {
    t2 = formatCheckedAt(check.createdAt);
    $[3] = check.createdAt;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t2) {
    t3 = <span className="text-xs font-normal text-muted-foreground">Checked {t2}</span>;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== isRechecking || $[8] !== onRecheck) {
    t4 = onRecheck && <RecheckButton onRecheck={onRecheck} isRechecking={isRechecking} label="Re-check" />;
    $[7] = isRechecking;
    $[8] = onRecheck;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t3 || $[11] !== t4) {
    t5 = <>{t3}{t4}</>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
}
const ELIGIBILITY_FIELDS: Field[] = [{
  key: "status",
  label: "Status"
}, {
  key: "authorization",
  label: "Authorization",
  colSpan: 2,
  wrap: true,
  hideIfEmpty: true
}, {
  key: "failureReason",
  label: "Failure Reason",
  colSpan: 3,
  wrap: true,
  hideIfEmpty: true
}, {
  key: "payerName",
  label: "Payer",
  colSpan: 3,
  wrap: true,
  hideIfEmpty: true
}, {
  key: "planType",
  label: "Plan Type",
  colSpan: 3,
  wrap: true,
  hideIfEmpty: true
}, {
  key: "planName",
  label: "Plan Name",
  colSpan: 3,
  wrap: true,
  hideIfEmpty: true
}, {
  key: "inNetwork",
  label: "Network",
  hideIfEmpty: true
}];
function StediEligibilityBody(t0) {
  const $ = _c(18);
  const {
    check
  } = t0;
  if (!check) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <p className="text-sm text-muted-foreground">An automated clearinghouse eligibility check has not been run or is currently pending for this patient. Prior authorization requirements may need manual verification.</p>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let display;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[1] !== check) {
    display = resolveStediEligibilityDisplay(check);
    const presentation = getEligibilityPresentation(display);
    const checkFailureReason = [display.primaryErrorCode, display.primaryErrorDescription].filter(Boolean).join(": ") || null;
    const fields = {
      status: presentation.statusLabel,
      authorization: display.checkFailed ? null : display.officeVisitRequiresAuth === true ? "Requires authorization" : display.officeVisitRequiresAuth === false ? "Does not require authorization" : "Authorization unknown",
      failureReason: display.checkFailed ? checkFailureReason : null,
      payerName: display.checkFailed ? null : display.payerName,
      planType: display.checkFailed ? null : display.planType,
      planName: display.checkFailed ? null : display.planName,
      inNetwork: display.checkFailed ? null : display.inNetwork === true ? "In network" : display.inNetwork === false ? "Out of network" : null
    };
    const pcps = display.pcps;
    t4 = "space-y-4";
    t1 = "space-y-2";
    t2 = <FieldLayout fields={ELIGIBILITY_FIELDS} patient={fields} />;
    t3 = display.checkFailed ? <p className="text-sm text-muted-foreground">No coverage determination was made. Re-check eligibility or verify coverage manually.</p> : <div className="text-sm">{pcps.length === 0 ? <div><span className="font-medium">PCP:</span> <CopyableContent text="Not supplied" /></div> : <div className="space-y-1.5"><span className="font-medium">{pcps.length === 1 ? "PCP:" : "PCPs:"}</span><div className="space-y-2 pl-2">{pcps.map(_temp)}</div></div>}</div>;
    $[1] = check;
    $[2] = display;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    display = $[2];
    t1 = $[3];
    t2 = $[4];
    t3 = $[5];
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t1 || $[8] !== t2 || $[9] !== t3) {
    t5 = <div className={t1}>{t2}{t3}</div>;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== display.checkFailed || $[12] !== display.officeVisitAuthReason) {
    t6 = !display.checkFailed && display.officeVisitAuthReason && <div className="text-sm"><div className="font-medium">Authorization Decision:</div><p className="mt-1 text-muted-foreground">{display.officeVisitAuthReason}</p></div>;
    $[11] = display.checkFailed;
    $[12] = display.officeVisitAuthReason;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t4 || $[15] !== t5 || $[16] !== t6) {
    t7 = <div className={t4}>{t5}{t6}</div>;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  return t7;
}
function _temp(p) {
  return <div key={`${p.name ?? ""}|${p.npi ?? ""}|${p.dates ?? ""}`} className="space-y-0.5 border-l-2 border-muted pl-2 text-xs"><div><CopyableContent text={p.name || "Unknown Provider"} />{p.dates && <span className="ml-1.5 text-muted-foreground">({p.dates}{p.isActive ? "" : " \u2022 Expired"})</span>}</div>{p.npi && <div className="text-muted-foreground">NPI: <CopyableContent text={p.npi} /></div>}</div>;
}
export function StediEligibilitySummary(t0) {
  const $ = _c(12);
  const {
    check,
    onRecheck,
    isRechecking: t1,
    showHeader: t2
  } = t0;
  const isRechecking = t1 === undefined ? false : t1;
  const showHeader = t2 === undefined ? true : t2;
  if (!showHeader) {
    let t3;
    if ($[0] !== check) {
      t3 = <StediEligibilityBody check={check} />;
      $[0] = check;
      $[1] = t3;
    } else {
      t3 = $[1];
    }
    return t3;
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <h3 className="font-semibold text-foreground">Insurance Eligibility</h3>;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== check || $[4] !== isRechecking || $[5] !== onRecheck) {
    t4 = <div className="mb-1 flex flex-wrap items-center gap-3">{t3}<StediEligibilityActions check={check} onRecheck={onRecheck} isRechecking={isRechecking} /></div>;
    $[3] = check;
    $[4] = isRechecking;
    $[5] = onRecheck;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== check) {
    t5 = <StediEligibilityBody check={check} />;
    $[7] = check;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t4 || $[10] !== t5) {
    t6 = <div className="mb-6 mt-4">{t4}{t5}</div>;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
}
function eligibilityCardVariant(check: StediEligibilityCheck | null): ValerieCardHeaderVariant {
  const display = check ? resolveStediEligibilityDisplay(check) : null;
  return display ? getEligibilityPresentation(display).cardVariant : "outline";
}
export function StediEligibilityCard(t0) {
  const $ = _c(10);
  const {
    check,
    actions,
    className
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Shield />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== check) {
    t2 = eligibilityCardVariant(check);
    $[1] = check;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== check) {
    t3 = <StediEligibilitySummary check={check} showHeader={false} />;
    $[3] = check;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== actions || $[6] !== className || $[7] !== t2 || $[8] !== t3) {
    t4 = <ValerieCard title="Insurance Eligibility" icon={t1} className={className} variant={t2} actions={actions}>{t3}</ValerieCard>;
    $[5] = actions;
    $[6] = className;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}